import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useInfiniteQuery } from '@tanstack/react-query';

import {
  governanceIssuesFilterParams,
  type GovernanceIssueResponse,
  type GovernanceIssuesFilters,
} from './useFetchGovernanceIssues';
import type { GovernanceIssuesGroupBy } from './useFetchGovernanceIssueGroups';

export interface GovernanceIssueGroupPage {
  data: GovernanceIssueResponse[];
  /** Opaque keyset cursor supplied by the API, or null after the last page. */
  nextCursor: string | null;
}

function toSearchParams({
  groupBy,
  groupKey,
  cursor,
  filters,
}: {
  groupBy: GovernanceIssuesGroupBy;
  groupKey: string;
  cursor: string | null;
  filters: GovernanceIssuesFilters;
}) {
  const params = governanceIssuesFilterParams(filters);
  params.set('groupBy', groupBy);
  params.set('groupKey', groupKey);

  if (cursor) {
    params.set('cursor', cursor);
  }

  return params;
}

/**
 * One independently paginated group of governance issues. Groups only fetch
 * after the user opens them, avoiding a request per group on initial render.
 */
export function useFetchGovernanceIssueGroupIssues(
  groupBy: GovernanceIssuesGroupBy,
  groupKey: string,
  filters: GovernanceIssuesFilters,
) {
  return useInfiniteQuery({
    queryKey: [api.governanceIssues, 'groupIssues', groupBy, groupKey, filters],
    queryFn: ({ pageParam }) =>
      fetchWrapper.get<GovernanceIssueGroupPage>(
        `${api.governanceIssues}/groups`,
        toSearchParams({ groupBy, groupKey, cursor: pageParam, filters }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // Keep the page current when a group is reopened after navigation.
    refetchOnMount: 'always',
  });
}
